import { ReactNode } from 'react'
import CN from 'classnames'

export interface SsccXmlColumn<T> {
  key: string
  header: string
  /** Grid track, e.g. '260px' or 'minmax(0,1fr)'. */
  width: string
  sortable?: boolean
  align?: 'left' | 'right'
  render: (row: T) => ReactNode
}

export interface SsccXmlSort {
  sortBy: string
  sortDirection: 'asc' | 'desc'
}

export interface SsccXmlTableProps<T> {
  columns: SsccXmlColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  totalCount: number
  pageSize: number
  pageNumber: number
  isLoading?: boolean
  sort?: SsccXmlSort
  onSortChange?: (sort: SsccXmlSort) => void
  onPageChange: (pageNumber: number) => void
  onPageSizeChange: (pageSize: number) => void
}

export const PAGE_SIZES = [10, 25, 50]

/** Pages shown around the current one, e.g. 1 … 4 5 6 … 12. */
const pageList = (current: number, last: number): (number | '…')[] => {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1)
  const pages: (number | '…')[] = [1]
  const from = Math.max(2, current - 1)
  const to = Math.min(last - 1, current + 1)
  if (from > 2) pages.push('…')
  for (let p = from; p <= to; p += 1) pages.push(p)
  if (to < last - 1) pages.push('…')
  pages.push(last)
  return pages
}

/**
 * BDL20-4066: the table + "Show rows per page" footer shared by the three SSCC XML tabs
 * (design: Manage Order - SSCC XML, screens 2/4/5).
 */
export const SsccXmlTable = <T,>({
  columns,
  rows,
  rowKey,
  totalCount,
  pageSize,
  pageNumber,
  isLoading,
  sort,
  onSortChange,
  onPageChange,
  onPageSizeChange,
}: SsccXmlTableProps<T>) => {
  const gridTemplateColumns = columns.map((c) => c.width).join(' ')
  const lastPage = Math.max(1, Math.ceil(totalCount / pageSize))
  const first = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const last = Math.min(totalCount, pageNumber * pageSize)
  const rangeLabel =
    totalCount === 0 ? '0 of 0' : `${first}–${last} of ${totalCount}`

  const toggleSort = (key: string) => {
    if (!onSortChange) return
    const sameColumn = sort?.sortBy === key
    onSortChange({
      sortBy: key,
      sortDirection:
        sameColumn && sort?.sortDirection === 'asc' ? 'desc' : 'asc',
    })
  }

  const pagerButton =
    'w-8 h-8 rounded border border-[#DADBDF] bg-white text-[#8A8F98] flex items-center justify-center disabled:opacity-40'

  return (
    <div className='border border-[#ECECEE] rounded-md overflow-hidden'>
      <div
        className='grid bg-[#F4F4F5] border-b border-[#E4E5E8] text-sm font-extrabold text-[#2A2A2D]'
        style={{ gridTemplateColumns }}>
        {columns.map((col) => (
          <div
            key={col.key}
            className={CN('px-[18px] py-[14px]', {
              'text-right': col.align === 'right',
            })}>
            {col.sortable ? (
              <button
                type='button'
                onClick={() => toggleSort(col.key)}
                className='inline-flex items-center gap-1 font-extrabold'>
                {col.header}
                <i
                  className={CN('text-base text-[#8A8F98]', {
                    'ri-arrow-up-down-line': sort?.sortBy !== col.key,
                    'ri-arrow-up-s-line':
                      sort?.sortBy === col.key && sort.sortDirection === 'asc',
                    'ri-arrow-down-s-line':
                      sort?.sortBy === col.key && sort.sortDirection === 'desc',
                  })}
                />
              </button>
            ) : (
              col.header
            )}
          </div>
        ))}
      </div>

      {rows.map((row) => (
        <div
          key={rowKey(row)}
          className='grid items-center border-b border-[#F0F0F2] text-sm text-[#3F3F46] min-h-[50px] hover:bg-[#FCF7F1]'
          style={{ gridTemplateColumns }}>
          {columns.map((col) => (
            <div
              key={col.key}
              className={CN('px-[18px] py-[10px] tabular-nums min-w-0', {
                'flex justify-end': col.align === 'right',
              })}>
              {col.render(row)}
            </div>
          ))}
        </div>
      ))}

      {!isLoading && rows.length === 0 && (
        <div className='p-10 text-center text-sm text-[#52525B]'>
          No orders match your search.
        </div>
      )}
      {isLoading && rows.length === 0 && (
        <div className='p-10 text-center text-sm text-[#52525B]'>Loading…</div>
      )}

      <div className='flex items-center gap-[10px] px-[18px] py-[14px] text-sm text-[#3F3F46]'>
        <label htmlFor='sscc-xml-page-size'>Show rows per page</label>
        <select
          id='sscc-xml-page-size'
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className='h-8 w-[78px] border border-[#DADBDF] rounded px-2 text-sm bg-white'>
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <div className='flex-1' />
        <span className='tabular-nums'>{rangeLabel}</span>
        <div className='flex gap-1'>
          <button
            type='button'
            aria-label='Previous page'
            className={pagerButton}
            disabled={pageNumber <= 1}
            onClick={() => onPageChange(pageNumber - 1)}>
            <i className='ri-arrow-left-s-line' />
          </button>
          {pageList(pageNumber, lastPage).map((p, i) =>
            p === '…' ? (
              <span
                key={`gap-${i}`}
                className='w-8 h-8 flex items-center justify-center text-[#8A8F98]'>
                …
              </span>
            ) : (
              <button
                key={p}
                type='button'
                onClick={() => onPageChange(p)}
                className={CN('w-8 h-8 rounded text-sm font-extrabold', {
                  'bg-[#F0A04B] text-white': p === pageNumber,
                  'border border-[#DADBDF] bg-white text-[#3F3F46]':
                    p !== pageNumber,
                })}>
                {p}
              </button>
            ),
          )}
          <button
            type='button'
            aria-label='Next page'
            className={pagerButton}
            disabled={pageNumber >= lastPage}
            onClick={() => onPageChange(pageNumber + 1)}>
            <i className='ri-arrow-right-s-line' />
          </button>
        </div>
      </div>
    </div>
  )
}

export default SsccXmlTable
